import React, { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, User, Bell, Trash2, Shield, Globe, Bug, Settings, UserCog, X, Check, Sparkles, CreditCard, Menu, LogOut } from 'lucide-react';
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../firebase';

export const SettingsLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentUser = auth.currentUser;

    const [isBugModalVisible, setIsBugModalVisible] = useState(false);
    const [bugText, setBugText] = useState('');
    const [isSubmittingBug, setIsSubmittingBug] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showLogoutMenu, setShowLogoutMenu] = useState(false);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            toast.error("Failed to log out");
        }
    };

    const handleSubmitBugReport = async () => {
        if (!bugText.trim()) {
            toast.error("Please describe the issue");
            return;
        }
        
        setIsSubmittingBug(true);
        try {
            let userData = {
                name: 'Anonymous',
                email: '',
                mobileNumber: '',
                uid: currentUser ? currentUser.uid : 'anonymous'
            };

            if (currentUser) {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    userData = {
                        name: data.name || '',
                        email: data.email || '',
                        mobileNumber: data.mobileNumber || '',
                        uid: currentUser.uid
                    };
                }
            }

            await addDoc(collection(db, 'feedbacks'), {
                name: userData.name,
                uid: userData.uid,
                email: userData.email,
                mobileNumber: userData.mobileNumber,
                feedback: `Via WebApp Reported a bug: ${bugText.trim()}`,
                timestamp: serverTimestamp(),
            });
            
            setIsBugModalVisible(false);
            setBugText('');
            setShowSuccessModal(true);
        } catch (error) {
            console.error(error);
            toast.error("Failed to submit report");
        } finally {
            setIsSubmittingBug(false);
        }
    };

    const handleOptionPress = (option) => {
        if (option.action) {
            option.action();
        } else if (option.navigateTo) {
            navigate(option.navigateTo);
        }
    };

    const settingsOptions = [
        { 
            name: 'Personal Details', 
            navigateTo: '/settings/personal', 
            icon: User, 
            color: '#FFFFFF' 
        },

        { 
            name: 'Creator Program', 
            navigateTo: '/settings/creator-program',
            icon: Sparkles, 
            color: '#BD00FF' 
        },
        { 
            name: 'Manage Subscription', 
            navigateTo: '/premium',
            icon: CreditCard, 
            color: '#0BD9E5' 
        },
        { 
            name: 'Notifications', 
            navigateTo: '/settings/notifications', 
            icon: Bell, 
            color: '#FFFFFF' 
        },
        { 
            name: 'Account Management', 
            navigateTo: '/settings/account', 
            icon: UserCog, 
            color: '#FFFFFF' 
        },
        { 
            name: 'Advanced Settings', 
            navigateTo: '/settings/advanced',
            icon: Globe, 
            color: '#FFFFFF' 
        },
        { 
            name: 'Report a Bug',
            action: () => setIsBugModalVisible(true),
            icon: Bug,
            color: '#ff4444'
        }
    ];

    const isSubRoute = location.pathname !== '/settings' && location.pathname.startsWith('/settings/');

    return (
        <div className="flex w-full sm:w-[1050px] shrink-0 min-h-screen bg-black">
            {/* MIDDLE COLUMN: Settings Menu — hide on mobile when sub-route is active */}
            <div className={`w-full sm:w-[350px] h-screen sticky top-0 border-r border-gray-800 flex flex-col shrink-0 ${isSubRoute ? 'hidden sm:flex' : 'flex'}`}>
                <div className="sticky top-0 bg-black/80 backdrop-blur-md z-40 border-b border-gray-800 flex items-center h-[60px] px-4">
                    <button onClick={() => navigate('/shadow-profile')} className="sm:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors mr-2">
                        <ChevronLeft size={24} className="text-white" />
                    </button>
                    <h1 className="text-xl font-extrabold text-white">Settings</h1>
                </div>

                <div className="flex-1 overflow-y-auto pb-20 sm:pb-0">
                    <div className="flex flex-col">
                        {settingsOptions.map((option) => {
                            const isActive = option.navigateTo && location.pathname.startsWith(option.navigateTo);
                            return (
                                <button
                                    key={option.name}
                                    onClick={() => handleOptionPress(option)}
                                    className={`w-full flex items-center gap-4 p-4 border-b border-gray-800 hover:bg-[#1a1a1a] transition-all cursor-pointer text-left ${isActive ? 'bg-[#1a1a1a] border-r-2 border-r-white' : 'bg-[#111]'}`}
                                >
                                    <option.icon size={20} color={option.color} />
                                    <span className="font-semibold text-[15px]" style={{ color: option.color }}>
                                        {option.name}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* RIGHT COLUMN: Settings Content — show on mobile when sub-route is active */}
            <div className={`w-full sm:w-[700px] shrink-0 relative flex-col ${isSubRoute ? 'flex' : 'hidden sm:flex'}`}>
                <Outlet />
            </div>

            {/* BUG REPORT MODAL */}
            {isBugModalVisible && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="w-full max-w-sm bg-[#111] border border-gray-800 rounded-3xl p-6 shadow-2xl relative">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-white">Report a Bug</h3>
                            <button onClick={() => setIsBugModalVisible(false)} className="text-gray-400 hover:text-white">
                                <X size={24} />
                            </button>
                        </div>
                        
                        <p className="text-gray-400 text-sm mb-6">
                            Found something broken? Let us know so we can fix it ASAP.
                        </p>

                        <textarea
                            value={bugText}
                            onChange={(e) => setBugText(e.target.value)}
                            placeholder="Describe the bug you encountered..."
                            className="w-full bg-[#1a1a1a] text-white p-4 rounded-xl border border-gray-700 focus:border-purple-500 focus:outline-none resize-none h-32 mb-6"
                            autoFocus
                        />

                        <button 
                            onClick={handleSubmitBugReport}
                            disabled={!bugText.trim() || isSubmittingBug}
                            className={`w-full py-4 rounded-full font-bold text-lg text-white transition-all
                                ${(!bugText.trim() || isSubmittingBug) 
                                    ? 'bg-purple-500/50 cursor-not-allowed' 
                                    : 'bg-purple-500 hover:bg-purple-600'}`}
                        >
                            {isSubmittingBug ? 'Sending...' : 'Submit Report'}
                        </button>
                    </div>
                </div>
            )}

            {/* SUCCESS MODAL */}
            {showSuccessModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="w-full max-w-sm bg-[#111] border border-gray-800 rounded-3xl p-8 shadow-2xl flex flex-col items-center text-center">
                        <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 flex items-center justify-center mb-6">
                            <Check size={32} color="#0BD9E5" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Report Sent!</h3>
                        <p className="text-gray-400 mb-8">
                            Thanks for helping us improve Spark. We'll look into this right away.
                        </p>
                        <button 
                            onClick={() => setShowSuccessModal(false)}
                            className="w-full py-4 rounded-full font-bold text-lg bg-gray-800 hover:bg-gray-700 text-white transition-all"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
